import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { BehaviorSubject, catchError, combineLatest, forkJoin, map, Observable, of, shareReplay, Subject, Subscription, switchMap, take, tap } from 'rxjs';
import { Member } from 'src/app/demo/model/member';
import { UserChat } from '../model/chat.model';
import { ChatService } from './chat.service';
import { StaffChatService } from './staff-chat.service';
import { StudentDTO } from '../model/StudentDTO';
import { RoomDTO } from '../model/RoomDTO';
import { LookupService } from './lookup.service';

@Injectable({
  providedIn: 'root',
})
export class MemberService {

  constructor(private http: HttpClient , private chatService : ChatService , private staffChatService : StaffChatService , private lookUpService : LookupService, private firestore: AngularFirestore) { }

  getMembers() {
    return this.http.get<any>('assets/demo/data/members.json')
      .toPromise()
      .then(res => res.data as Member[])
      .then(data => data);
  }

  private nameSubject = new BehaviorSubject<{ firstName: string, lastName: string } | null>(null);
  name$ = this.nameSubject.asObservable();

  private showDialogSource = new BehaviorSubject<boolean>(false);
  showDialog$ = this.showDialogSource.asObservable();

  private visibleFieldsSource = new BehaviorSubject<string[]>([]);
  visibleFields$ = this.visibleFieldsSource.asObservable();

  private topbarRefreshSubject = new Subject<boolean>();
  topbarRefresh$ = this.topbarRefreshSubject.asObservable();

  private unreadCountSubject = new BehaviorSubject<number>(0);
  unreadCount$ = this.unreadCountSubject.asObservable();

  private studentListCache$?: Observable<StudentDTO[]>;
  private roomListCache$?: Observable<RoomDTO[]>;
  private unreadCountSubscription?: Subscription;
  
  setUnreadCount(count: number): void {
    this.unreadCountSubject.next(count);
  }
  
getUnreadCount(userId: string): Observable<number> {
  return forkJoin([
    this.chatService.getUserChats(userId).pipe(
      switchMap(chats =>
        forkJoin(chats.map(chat =>
          this.chatService.getUnreadCount(chat.chatId, userId).pipe(catchError(() => of(0)))
        ))
      ),
      map(counts => counts.reduce((a, b) => a + b, 0))
    ),
    this.staffChatService.getUserChats(userId).pipe(
      switchMap(chats =>
        forkJoin(chats.map(chat =>
          this.staffChatService.getUnreadCount(chat.chatId, userId).pipe(catchError(() => of(0)))
        ))
      ),
      map(counts => counts.reduce((a, b) => a + b, 0))
    )
  ]).pipe(
    map(([parentCount, staffCount]) => parentCount + staffCount),
    tap(total => {
      this.setUnreadCount(total);
    })
  );
}



  updateName(firstName: string, lastName: string): void {
    this.nameSubject.next({ firstName, lastName });
  }


  triggerTopbarRefresh(): void {
    this.topbarRefreshSubject.next(true);
  }

  getSchoolDetailsChanged$(): Observable<boolean> {
    return this.topbarRefresh$;
  }


  openDialog(visibleFields: string[] = []) {
    this.visibleFieldsSource.next(visibleFields);
    this.showDialogSource.next(true);
  }

  closeDialog() {
    this.showDialogSource.next(false);
  }


  
  getStudents(schoolId: number): Observable<StudentDTO[]> {
    if (!this.studentListCache$) {
      const search = { 'profileStatus.notEquals': 'I', 'schoolId.equals': schoolId, sort: 'fullName,asc' };
      this.studentListCache$ = this.lookUpService.queryStudent(search).pipe(
        map(res => res.body ?? []),
        shareReplay(1)
      );
    }
    return this.studentListCache$;
  }

  getRooms(): Observable<RoomDTO[]> {
    if (!this.roomListCache$) {
      this.roomListCache$ = this.lookUpService.queryRooms({}).pipe(
        map(res => res.body ?? []),
        shareReplay(1)
      );
    }
    return this.roomListCache$;
  }



  // Single Firebase call per user using aggregated counts document
  calculateUnreadCounts(userData: any): void {
    // Clean up previous subscription
    this.unreadCountSubscription?.unsubscribe();

    const userId = userData?.refTable?.toLowerCase() === 'staff'
    ? `staff_${userData.refTableId}`
    : `parent_${userData.refTableId}`;
    const isStaff = userData?.refTable?.toLowerCase() === 'staff';

    // First try aggregated document, fallback to individual queries if needed
    this.unreadCountSubscription = this.firestore
        .doc(`userCounts/${userId}`)
        .valueChanges()
        .pipe(
            map((counts: any) => {
                if (!counts) return null; // Signal to use fallback
                // For staff: parentCount + staffCount
                // For parents: only parentCount
                if (isStaff) {
                    return (counts.parentUnreadCount || 0) + (counts.staffUnreadCount || 0);
                } else {
                    return counts.parentUnreadCount || 0;
                }
            }),
            catchError(() => of(null)), // Use fallback on error
            switchMap((aggregatedCount: number | null) => {
                if (aggregatedCount !== null) {
                    // Use aggregated count
                    return of(aggregatedCount);
                } else {
                    // Fallback to individual queries
                    return this.getFallbackMessageCount(userId, isStaff);
                }
            }),
            tap((total: number) => this.setUnreadCount(total))
        )
        .subscribe();
  }

  private getFallbackMessageCount(userId: string, isStaff: boolean): Observable<number> {
    if (isStaff) {
        // For staff: query BOTH collections with real-time updates
        const parentChatsQuery: Observable<number> = this.firestore
            .collection('userChats')
            .doc(userId)
            .collection<UserChat>('chats', ref =>
                ref.where('unreadCount', '>', 0)
            )
            .valueChanges()
            .pipe(
              map(chats => chats.length),
                catchError(() => of(0))
            );

        const staffChatsQuery: Observable<number> = this.firestore
            .collection('staffUserChats')
            .doc(userId)
            .collection<UserChat>('staffChats', ref =>
                ref.where('unreadCount', '>', 0)
            )
            .valueChanges()
            .pipe(
              map(chats => chats.length),
                catchError(() => of(0))
            );

        // Combine both with real-time updates
        return combineLatest([parentChatsQuery, staffChatsQuery]).pipe(
            map(([parentCount, staffCount]: [number, number]) => parentCount + staffCount)
        );
    } else {
        // For parents: query only /userChats/{parent_id}/chats with real-time updates
        return this.firestore
            .collection('userChats')
            .doc(userId)
            .collection<UserChat>('chats', ref =>
                ref.where('unreadCount', '>', 0)
            )
            .valueChanges()
            .pipe(
                map(chats => chats.reduce((total, chat) => total + (chat.unreadCount || 0), 0)),
                catchError(() => of(0))
            );
    }
  }

  // Cleanup method for logout
  clearUnreadCountSubscription(): void {
    this.unreadCountSubscription?.unsubscribe();
    this.unreadCountSubscription = undefined;
  }

}
